// apps/admin/src/components/composer/preview-renderer.tsx
//
// P5 UI Polish W5 T5 — Admin-side PreviewRenderer (spec §2.1).
// P5 UI Polish W6 T4 — Real image/video preview via signed URLs (spec §4).
// P5 UI Polish W6 T7 — Live preview refresh: RSS auto-poll + web_page sandbox
//   widening (spec §7).
//
// Renders each zone's actual content type in a static preview:
//   - rss_feed  → sandboxed <iframe src={source_url}> or source_url label;
//                 re-polls (remounts the iframe) every 60s via useInterval
//                 while the document is visible (Page Visibility API pauses
//                 the interval when document.hidden = true).
//   - web_page  → sandboxed <iframe src={source_url}>
//   - image     → <img src={signed_url}> when signed_url present; else badge
//   - video     → <video poster={poster_signed_url ?? signed_url}> when URL
//                 present; else content-type badge.
//                 NOTE: signed_url for video points at the video file; the
//                 browser may render a first-frame poster or leave it blank
//                 (degraded but still better than a badge).
//   - audio     → content-type badge
//   - unbound   → gradient placeholder + zone label (W2 T7 fallback)
//
// W6 T3 ships the /signed-url endpoint; T4 wires it here via PreviewPane.
// DS §5 invariants: no signing verification, no Ed25519, no telemetry,
// no playback-event reporting — admin preview only.
// Player runtime (apps/player/src/) is byte-untouched.
//
// SECURITY NOTE (W6 T7): the `<iframe sandbox="allow-same-origin allow-scripts">`
// in the web_page branch widens W5 T5's max-restriction sandbox to allow real
// interactive preview. This is ADMIN-PREVIEW ONLY: PreviewRenderer never runs
// in the player runtime, content URLs come from the same tenant (validated
// server-side at composer-edit time), and the iframe is never loaded outside
// this component. DS §5 invariants (signing, manifest verification) are
// untouched. The RSS iframe sandbox intentionally remains "" (empty / maximum
// restriction) because the RSS feed URL is third-party; only web_page zones
// are tenant-scoped content warranting the widening.

import { useState } from 'react';
import type { Zone } from '@displaycms/contracts';
import type { ResourceKind } from '@displaycms/contracts';
import { useInterval } from '@/hooks/use-interval';

// ---------------------------------------------------------------------------
// Content binding for a zone (admin-side, resolved by the caller)
// ---------------------------------------------------------------------------

export interface ZoneContentBinding {
  /** Resource kind — drives which renderer is chosen. */
  kind: ResourceKind;
  /** source_url for rss_feed / web_page dynamic resources. Absent for static. */
  source_url?: string | null;
  /** Human-readable name to show for static assets (image/video/audio). */
  label?: string;
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface PreviewRendererProps {
  zones: ReadonlyArray<Zone & { name_i18n?: { fr?: string; en?: string } }>;
  /** Optional: pre-resolved content bindings keyed by zone id.
   *  Zones absent from this map render the gradient placeholder. */
  contentMap?: Record<string, ZoneContent>;
  canvasWidth?: number;
  canvasHeight?: number;
}

export interface ZoneContent {
  kind: ResourceKind;
  /** For rss_feed / web_page: the external URL to embed in a sandboxed iframe. */
  source_url?: string | null;
  /** For image / video / audio: a human-readable display name (no raw CDN URL). */
  label?: string;
  /** W6 T4 — Short-TTL signed URL for image/video static assets (from T3
   *  getResourceSignedUrl). When present, renders <img>/<video> instead of
   *  the content-type badge. Absent → badge fallback (no error). */
  signed_url?: string;
  /** W6 T4 — Optional separate signed URL for a video thumbnail/poster image.
   *  When absent on a video zone, signed_url is used as the poster src
   *  (browsers will either render the first frame or leave the slot blank). */
  poster_signed_url?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PreviewRenderer({
  zones,
  contentMap = {},
  canvasWidth = 1920,
  canvasHeight = 1080,
}: PreviewRendererProps): JSX.Element {
  return (
    <div
      className="relative bg-black"
      style={{ width: '100%', aspectRatio: `${canvasWidth} / ${canvasHeight}` }}
      data-testid="preview-renderer"
    >
      {zones.map((zone) => (
        <PreviewZone
          key={zone.id}
          zone={zone}
          content={contentMap[zone.id] ?? null}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-zone renderer
// ---------------------------------------------------------------------------

interface PreviewZoneProps {
  zone: Zone & { name_i18n?: { fr?: string; en?: string } };
  content: ZoneContent | null;
  canvasWidth: number;
  canvasHeight: number;
}

function PreviewZone({
  zone,
  content,
  canvasWidth,
  canvasHeight,
}: PreviewZoneProps): JSX.Element {
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${(zone.x / canvasWidth) * 100}%`,
    top: `${(zone.y / canvasHeight) * 100}%`,
    width: `${(zone.w / canvasWidth) * 100}%`,
    height: `${(zone.h / canvasHeight) * 100}%`,
    overflow: 'hidden',
  };

  const zoneLabel = zone.name_i18n?.fr ?? zone.id;

  // ── Unbound zone: gradient placeholder + zone label ──────────────────────
  if (!content) {
    return (
      <div
        style={{
          ...style,
          background:
            'linear-gradient(135deg, hsl(var(--muted)), hsl(var(--secondary)))',
          color: 'hsl(var(--muted-foreground))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 14,
          border: '1px solid hsl(var(--border))',
          textAlign: 'center',
          padding: '8px',
        }}
        title={zoneLabel}
        data-testid={`preview-zone-${zone.id}-empty`}
      >
        {zoneLabel}
      </div>
    );
  }

  // ── web_page: sandboxed iframe ────────────────────────────────────────────
  // W6 T7: sandbox widened to "allow-same-origin allow-scripts" for admin
  // preview only (see SECURITY NOTE at the top of this file). The RSS iframe
  // below intentionally keeps sandbox="" (third-party content).
  if (content.kind === 'web_page') {
    if (content.source_url) {
      return (
        <iframe
          src={content.source_url}
          sandbox="allow-same-origin allow-scripts"
          style={style}
          className="border-0"
          title={zoneLabel}
          data-testid={`preview-zone-${zone.id}-web`}
        />
      );
    }
    return (
      <ContentTypeBadge
        style={style}
        label={content.label ?? zoneLabel}
        badge="Page web"
        testId={`preview-zone-${zone.id}-web`}
      />
    );
  }

  // ── rss_feed: sandboxed iframe with auto-poll (W6 T7) ───────────────────
  // Increments a counter every 60s to remount the iframe (key-bump), causing
  // the browser to re-fetch the feed. Paused when document.hidden = true
  // (Page Visibility API) so background tabs don't burn the network.
  // sandbox remains "" (maximum restriction) — RSS feeds are third-party URLs.
  if (content.kind === 'rss_feed') {
    if (content.source_url) {
      return (
        <RssFeedZone
          source_url={content.source_url}
          zoneId={zone.id}
          style={style}
          title={zoneLabel}
        />
      );
    }
    return (
      <ContentTypeBadge
        style={style}
        label={content.label ?? zoneLabel}
        badge="RSS"
        testId={`preview-zone-${zone.id}-rss`}
      />
    );
  }

  // ── image: <img> when signed_url present; badge otherwise ────────────────
  if (content.kind === 'image') {
    if (content.signed_url) {
      return (
        <img
          src={content.signed_url}
          alt={content.label ?? ''}
          style={{ ...style, objectFit: 'cover' }}
          data-testid={`preview-zone-${zone.id}-image`}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      );
    }
    return (
      <ContentTypeBadge
        style={style}
        label={content.label ?? zoneLabel}
        badge="Image"
        testId={`preview-zone-${zone.id}-image`}
      />
    );
  }

  // ── video: <video poster> when signed_url present; badge otherwise ────────
  if (content.kind === 'video') {
    const posterSrc = content.poster_signed_url ?? content.signed_url;
    if (posterSrc) {
      return (
        <video
          poster={posterSrc}
          muted
          controls={false}
          autoPlay={false}
          style={style}
          data-testid={`preview-zone-${zone.id}-video`}
        />
      );
    }
    return (
      <ContentTypeBadge
        style={style}
        label={content.label ?? zoneLabel}
        badge="Vidéo"
        testId={`preview-zone-${zone.id}-video`}
      />
    );
  }

  // ── audio: <audio controls> when signed_url present; badge otherwise ───────
  if (content.kind === 'audio') {
    if (content.signed_url) {
      return (
        <div
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'hsl(var(--muted))',
            padding: '8px',
          }}
          data-testid={`preview-zone-${zone.id}-audio`}
        >
          <audio src={content.signed_url} controls style={{ width: '100%' }} />
        </div>
      );
    }
    return (
      <ContentTypeBadge
        style={style}
        label={content.label ?? zoneLabel}
        badge="Audio"
        testId={`preview-zone-${zone.id}-audio`}
      />
    );
  }

  // ── Fallback for any future resource kind ────────────────────────────────
  return (
    <div
      style={{
        ...style,
        background:
          'linear-gradient(135deg, hsl(var(--muted)), hsl(var(--secondary)))',
        color: 'hsl(var(--muted-foreground))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 14,
        border: '1px solid hsl(var(--border))',
        textAlign: 'center',
        padding: '8px',
      }}
      title={zoneLabel}
      data-testid={`preview-zone-${zone.id}-empty`}
    >
      {zoneLabel}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RSS feed zone — auto-polls every 60 s via useInterval + key-bump (W6 T7)
// ---------------------------------------------------------------------------

function RssFeedZone({
  source_url,
  zoneId,
  style,
  title,
}: {
  source_url: string;
  zoneId: string;
  style: React.CSSProperties;
  title: string;
}): JSX.Element {
  // Increment tick every 60 s to remount the iframe (key-bump re-fetches feed).
  const [tick, setTick] = useState(0);

  useInterval(() => {
    setTick((n) => n + 1);
  }, 60_000);

  return (
    <iframe
      key={tick}
      src={source_url}
      // Keep sandbox="" (maximum restriction) — RSS feed URLs are third-party
      // content; scripts must not run (see SECURITY NOTE at file top).
      sandbox=""
      style={style}
      className="border-0"
      title={title}
      data-testid={`preview-zone-${zoneId}-rss`}
    />
  );
}

// ---------------------------------------------------------------------------
// Content-type badge helper
// ---------------------------------------------------------------------------

function ContentTypeBadge({
  style,
  label,
  badge,
  testId,
}: {
  style: React.CSSProperties;
  label: string;
  badge: string;
  testId: string;
}): JSX.Element {
  return (
    <div
      style={{
        ...style,
        background: 'hsl(var(--secondary))',
        color: 'hsl(var(--secondary-foreground))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 12,
        border: '1px solid hsl(var(--border))',
        textAlign: 'center',
        padding: '8px',
        gap: '4px',
      }}
      title={label}
      data-testid={testId}
    >
      <span
        style={{
          background: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {badge}
      </span>
      <span style={{ opacity: 0.7, fontSize: 11 }}>{label}</span>
    </div>
  );
}
